import { useState } from "react";
import { useCachedResource } from "../hooks/useCachedResource";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { NeuButton, NeuCard } from "../components/neu/Neu";
import { SalesShell } from "../features/sales/SalesShell";
import { DocRow } from "../features/sales/DocRow";
import { DocDetail } from "../features/sales/DocDetail";
import { InvoiceActions } from "../features/sales/InvoiceActions";
import { getDoc, listDocs, openDeliveries } from "../api/sales";
import { dispatchEinvoices, einvoiceJobs, invoiceFromDelivery } from "../api/salesInvoices";

// الفواتير الضريبية: إنشاء من تسليم · إصدار (ترقيم + PINT + قيد) · طابور الإرسال إلى ASP.
export default function SalesInvoicesPage() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(null);

  const { data, reload: load } = useCachedResource("sales-invoices", async () => ({
    rows: (await listDocs({ doc_type: "invoice" })).items || [],
    pending: await openDeliveries().catch(() => []),
    jobs: await einvoiceJobs().catch(() => null),
  }));
  const rows = data?.rows || [];
  const pending = data?.pending || [];
  const jobs = data?.jobs || null;

  const createFrom = async (delivery) => {
    try {
      await invoiceFromDelivery({ delivery_id: delivery.id, date: new Date().toISOString().slice(0, 10) });
      toast.success(t("sales.invoice.created"));
      load();
    } catch (error) {
      toast.error(error?.response?.data?.detail?.code || t("errors.generic"));
    }
  };

  const dispatch = async () => {
    const result = await dispatchEinvoices();
    toast.success(t("sales.invoice.dispatched", { count: result.processed, provider: result.provider }));
    load();
  };

  return (
    <SalesShell titleKey="invoices">
      <NeuCard className="grid gap-3 p-4" data-testid="invoice-pending-deliveries">
        <p className="label-xs">{t("sales.invoice.fromDelivery")}</p>
        {pending.length ? pending.map((row) => (
          <div key={row.id} className="flex flex-wrap items-center gap-3 text-xs">
            <span className="min-w-0 flex-1 truncate font-bold">{row.customer} · {row.date}</span>
            <span className="num font-mono font-bold">{row.payable}</span>
            <NeuButton className="px-3 py-1 text-xs" data-testid={`invoice-create-${row.id}`}
                       onClick={() => createFrom(row)}>
              {t("sales.invoice.create")}
            </NeuButton>
          </div>
        )) : <p className="text-xs text-muted-foreground">{t("sales.invoice.noPending")}</p>}
      </NeuCard>

      <NeuCard className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid="einvoice-queue">
        <p className="text-xs font-bold">
          {t("sales.invoice.queue")}: <span className="num">{jobs?.total ?? 0}</span>
        </p>
        <NeuButton className="px-3 py-2 text-xs" data-testid="einvoice-dispatch" onClick={dispatch}>
          {t("sales.invoice.dispatch")}
        </NeuButton>
      </NeuCard>

      <div className="grid gap-2" data-testid="invoice-list">
        {rows.map((row) => (
          <DocRow key={row.id} doc={row} onOpen={async () => setOpen(await getDoc(row.id))} />
        ))}
        {!rows.length ? (
          <p className="text-sm text-muted-foreground" data-testid="no-invoices">{t("sales.invoice.none")}</p>
        ) : null}
      </div>

      {open ? (
        <div className="grid gap-3">
          <InvoiceActions doc={open} onDone={async () => { setOpen(await getDoc(open.id)); load(); }} />
          <DocDetail doc={open} />
        </div>
      ) : null}
    </SalesShell>
  );
}
